import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../http';
import './AdminLanding.css';
import CreateEventWizard from './wizards/CreateEventWizard';
import GlobalLoginModal from '../GlobalLoginModal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAuth } from '../context/AuthContext';

const AdminLanding = () => {
  const navigate = useNavigate();
  const [activeEvents, setActiveEvents] = useState([]);
  const [setupEvents, setSetupEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [alertMsg, setAlertMsg] = useState(null);
  const { sessionExpiredRole, clearSessionExpired } = useAuth();

  const fetchEvents = async () => {
    try {
      // Fetch ALL non-archived events
      const res = await api.get('/api/test-events');
      // Filter client-side
      const allEvents = Array.isArray(res.data) ? res.data : [];
      setActiveEvents(allEvents.filter((e) => e.status === 'active'));
      setSetupEvents(allEvents.filter((e) => e.status === 'setup'));
    } catch (err) {
      console.error('Failed to fetch events', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleActivateTest = (eventId, eventName) => {
    setConfirm({
      message: `Are you sure you want to ACTIVATE "${eventName}"? This will make it live for proctors.`,
      confirmLabel: 'Activate',
      onConfirm: async () => {
        try {
          await api.post(`/api/test-events/${eventId}/activate`);
          setAlertMsg({ type: 'success', text: 'Test activated successfully!' });
          fetchEvents();
        } catch (e) {
          setAlertMsg({ type: 'danger', text: 'Failed to activate: ' + (e.response?.data?.error || e.message) });
        }
      }
    });
  };

  const handleCloseTest = (eventId, eventName) => {
    setConfirm({
      message: `Are you sure you want to CLOSE "${eventName}"? This will archive the event and clear it from the dashboard.`,
      confirmLabel: 'Close',
      destructive: true,
      onConfirm: async () => {
        try {
          await api.post(`/api/test-events/${eventId}/archive`);
          setAlertMsg({ type: 'success', text: 'Test closed (archived) successfully.' });
          fetchEvents();
        } catch (e) {
          setAlertMsg({ type: 'danger', text: 'Failed to close test: ' + (e.response?.data?.error || e.message) });
        }
      }
    });
  };

  const handleDeleteTest = (eventId, eventName) => {
    setConfirm({
      message: `Are you sure you want to completely DELETE "${eventName}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
      onConfirm: async () => {
        try {
          await api.delete(`/api/test-events/${eventId}`);
          setAlertMsg({ type: 'success', text: 'Test deleted successfully.' });
          setActiveEvents((prev) => prev.filter(e => e.id !== eventId));
          setSetupEvents((prev) => prev.filter(e => e.id !== eventId));
        } catch (e) {
          setAlertMsg({ type: 'danger', text: 'Failed to delete test: ' + (e.response?.data?.error || e.message) });
        }
      }
    });
  };

  return (
    <div className="admin-landing-container">
      <ConfirmDialog confirm={confirm} onCancel={() => setConfirm(null)} />
      {alertMsg && (
        <div style={{
          position: 'fixed', top: 16, right: 16, zIndex: 3000,
          padding: '12px 20px', borderRadius: 8,
          background: alertMsg.type === 'success' ? '#d4edda' : '#f8d7da',
          color: alertMsg.type === 'success' ? '#155724' : '#721c24',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          {alertMsg.text}
          <button onClick={() => setAlertMsg(null)} style={{ marginLeft: 12, background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      )}
      {sessionExpiredRole === 'admin' && (
        <GlobalLoginModal
          role="admin"
          onLoginSuccess={clearSessionExpired}
        />
      )}
      {showCreateWizard && (
        <CreateEventWizard
          onComplete={() => {
            setShowCreateWizard(false);
            fetchEvents();
          }}
          onCancel={() => setShowCreateWizard(false)}
        />
      )}
      <header className="admin-header">
        <h1>Evolution Admin</h1>
        <button
          className="logout-btn"
          onClick={() => {
            navigate('/');
          }}
        >
          Logout
        </button>
      </header>

      {/* Active Tests Hero Section */}
      <section
        className="admin-hero-card"
        style={{
          borderLeft:
            activeEvents.length > 0 || setupEvents.length > 0
              ? '5px solid #28a745'
              : '5px solid #cbd5e0'
        }}
      >
        <div style={{ width: '100%' }}>
          {/* Active Events */}
          <div
            className="hero-header"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem'
            }}
          >
            <div>
              <h2>Active & Upcoming Tests</h2>
              <p style={{ margin: 0 }}>Currently running or scheduled certification exams.</p>
            </div>
          </div>

          {loading ? (
            <div className="status-message">Checking status...</div>
          ) : (
            <>
              {/* ACTIVE Section */}
              {activeEvents.length > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                  <h3
                    style={{
                      color: '#2f855a',
                      borderBottom: '1px solid #eee',
                      paddingBottom: '0.5rem'
                    }}
                  >
                    🟢 Active Now
                  </h3>
                  <div className="active-tests-list">
                    {activeEvents.map((event) => (
                      <div key={event.id} className="active-test-row">
                        <div className="test-info">
                          <span className="test-name">{event.name}</span>
                          <span className="test-date">
                            {new Date(event.event_date).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="test-actions">
                          <Link to="/dashboard" className="btn btn-sm btn-primary">
                            Dashboard
                          </Link>
                          <Link to="/admin/manual-entry" className="btn btn-sm btn-secondary">
                            Manual Data Entry
                          </Link>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleCloseTest(event.id, event.name)}
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* SETUP Section */}
              {setupEvents.length > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                  <h3
                    style={{
                      color: '#DD6B20',
                      borderBottom: '1px solid #eee',
                      paddingBottom: '0.5rem'
                    }}
                  >
                    🟠 Upcoming (Setup)
                  </h3>
                  <div className="active-tests-list">
                    {setupEvents.map((event) => (
                      <div
                        key={event.id}
                        className="active-test-row"
                        style={{ borderLeft: '3px solid #DD6B20' }}
                      >
                        <div className="test-info">
                          <span className="test-name">{event.name}</span>
                          <span className="test-date">
                            {new Date(event.event_date).toLocaleDateString()}
                          </span>
                          <span style={{ fontSize: '0.8rem', color: '#666', marginLeft: '1rem' }}>
                            Status: Setup
                          </span>
                        </div>
                        <div className="test-actions">
                          <Link
                            to={`/admin/events/${event.id}/edit`}
                            className="btn btn-sm btn-primary"
                            style={{ marginRight: '0.25rem' }}
                          >
                            Edit
                          </Link>
                          <button
                            className="btn btn-sm btn-success"
                            style={{
                              background: parseInt(event.station_count) > 0 ? '#38a169' : '#a0aec0',
                              color: 'white',
                              cursor: parseInt(event.station_count) > 0 ? 'pointer' : 'not-allowed'
                            }}
                            onClick={() =>
                              parseInt(event.station_count) > 0 &&
                              handleActivateTest(event.id, event.name)
                            }
                            disabled={parseInt(event.station_count) === 0}
                            title={
                              parseInt(event.station_count) === 0
                                ? 'You must add at least one station via Edit before activating.'
                                : ''
                            }
                          >
                            Activate
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteTest(event.id, event.name)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeEvents.length === 0 && setupEvents.length === 0 && (
                <div className="no-active-events">
                  <p style={{ fontStyle: 'italic', color: '#666', margin: '1rem 0' }}>
                    No active or upcoming tests.
                  </p>
                  <div className="card-actions">
                    <Link to="/admin/manual-entry" className="btn btn-secondary">
                      Manual Data Entry
                    </Link>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <div className="admin-grid">
        {/* Test Management Section */}
        <section className="admin-card">
          <h2>Test Events</h2>
          <p>Manage upcoming certification exams.</p>
          <div className="card-actions">
            <button className="btn btn-primary" onClick={() => setShowCreateWizard(true)}>
              + Create New Test
            </button>
            <Link to="/admin/history" className="btn btn-secondary">
              Test History
            </Link>
          </div>
        </section>

        {/* Library Management Section */}
        <section className="admin-card">
          <h2>Libraries</h2>
          <p>Configure reusable components for tests.</p>
          <div className="card-actions">
            <Link to="/admin/skills" className="btn btn-secondary">
              Manage Skills
            </Link>
            <Link to="/admin/templates" className="btn btn-secondary">
              Station Templates
            </Link>
            <Link to="/admin/proctors" className="btn btn-secondary">
              Proctor List
            </Link>
          </div>
        </section>

        {/* System Settings Section */}
        <section className="admin-card">
          <h2>System</h2>
          <p>Global configuration and defaults.</p>
          <div className="card-actions">
            <Link to="/admin/settings" className="btn btn-secondary">
              Global Settings
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminLanding;
